import React, { useEffect, useMemo, useState } from "react";
import { X, Send, Loader2, CheckCircle2, Users, Briefcase, GraduationCap, Hotel, HeartPulse, Sparkles, Landmark, Palette } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// ---------------------------------------------------------------------------
// VolumeLaptopInquiryModal — short, plain-language inquiry form for the
// Volume Laptop Solutions section on /workstations. Deliberately does NOT
// ask about processors / RAM / GPU / storage — Ur Designs selects the
// hardware manually based on the customer's needs.
//
// Submits to POST /api/volume-laptop-requests (reuses the shared Resend
// notification pipeline). No pricing, no delivery / warranty promises,
// no auto-hardware recommendation surfaced back to the customer.
// ---------------------------------------------------------------------------

const INDUSTRIES = [
  { id: "business", label: "Business & Corporate", icon: Briefcase },
  { id: "education", label: "Education", icon: GraduationCap },
  { id: "government", label: "Government", icon: Landmark },
  { id: "hospitality", label: "Hospitality", icon: Hotel },
  { id: "healthcare", label: "Healthcare", icon: HeartPulse },
  { id: "creative", label: "Creative Team", icon: Palette },
  { id: "other", label: "Other", icon: Sparkles },
];

const INTENDED_USES = [
  { id: "office", label: "Office / Business" },
  { id: "design", label: "Graphic Design / Marketing" },
  { id: "architecture", label: "Architecture / Engineering / 3D" },
  { id: "mixed", label: "Mixed Team Requirements" },
  { id: "other", label: "Other" },
];

const PLATFORMS = [
  { id: "windows", label: "Windows" },
  { id: "mac", label: "Mac" },
  { id: "mixed", label: "Mix of Windows & Mac" },
  { id: "not_sure", label: "Not Sure" },
];

const SETUP_LEVELS = [
  {
    id: "supply_only",
    label: "Laptops Only",
    sub: "Hardware supplied without software configuration.",
  },
  {
    id: "ready_to_work",
    label: "Ready-to-Work Setup",
    sub: "Laptops prepared with the required software and configuration for your team.",
    recommended: true,
  },
  {
    id: "not_sure",
    label: "Recommend the Best Setup",
    sub: "Tell us what your team does and we'll recommend what makes sense.",
  },
];

const emptyForm = () => ({
  organization_name: "",
  industry: "",
  industry_other: "",
  quantity: "",
  // Legacy singular (kept for backward compat with the backend API +
  // historical records). Populated from the primary/first entry of
  // `intended_uses` on submit.
  intended_use: "",
  intended_use_other: "",
  intended_uses: [], // new multi-select — subset of INTENDED_USES
  platform: "", // customer-optional; defaults to "not_sure" on submit
  setup_level: "",
  software_needs: "",
  timing: "",
  additional_info: "",
  contact: { name: "", email: "", phone: "" },
});

export const VolumeLaptopInquiryModal = ({ open, onClose }) => {
  const [form, setForm] = useState(emptyForm);
  const [step, setStep] = useState("form"); // form | review | done
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(emptyForm());
      setStep("form");
      setDone(null);
    }
  }, [open]);

  const requireOtherIndustry = form.industry === "other";
  const requireOtherUse = (form.intended_uses || []).includes("other");

  const canSubmit = useMemo(() => {
    if (!form.organization_name.trim()) return false;
    if (!form.industry) return false;
    if (requireOtherIndustry && !form.industry_other.trim()) return false;
    if (!form.quantity || Number(form.quantity) < 1) return false;
    if ((form.intended_uses || []).length === 0) return false;
    if (requireOtherUse && !form.intended_use_other.trim()) return false;
    // platform is CUSTOMER-OPTIONAL — defaults to "not_sure" on submit.
    if (!form.setup_level) return false;
    if (!form.contact.name.trim()) return false;
    if (!form.contact.email.includes("@")) return false;
    if (!form.contact.phone.trim()) return false;
    return true;
  }, [form, requireOtherIndustry, requireOtherUse]);

  const setField = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setContact = (patch) => setForm((f) => ({ ...f, contact: { ...f.contact, ...patch } }));

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const uses = form.intended_uses || [];
      const primary = uses[0]; // legacy singular = first selection
      const payload = {
        ...form,
        quantity: Number(form.quantity),
        // Backward-compatible: singular intended_use = first choice;
        // full multi-select ships as intended_uses[].
        intended_use: primary,
        intended_uses: uses,
        // Customer-optional platform: default to "not_sure" when the
        // shopper left it blank.
        platform: form.platform || "not_sure",
      };
      const r = await api.post("/volume-laptop-requests", payload);
      setDone({ organization_name: r.data.organization_name, id: r.data.request_id });
      setStep("done");
      toast.success("Volume request received.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="volume-laptop-modal"
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 sm:p-6"
    >
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl bg-[#111111] border border-[#262626] shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          data-testid="volume-laptop-modal-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full grid place-items-center bg-[#1A1A1A] border border-[#262626] hover:border-[#FF6600] transition-colors z-10"
        >
          <X className="w-4 h-4" />
        </button>

        {step === "done" ? (
          <div className="p-8 sm:p-10" data-testid="volume-laptop-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-400" strokeWidth={2} />
            <h2 className="mt-4 font-display text-2xl font-bold">Request received.</h2>
            <p className="mt-3 text-sm text-[#A3A3A3] leading-relaxed">
              Thank you — we've received your volume request for{" "}
              <span className="text-white font-semibold">{done?.organization_name}</span>.
              Our team will review the requirements and follow up with a
              tailored recommendation and estimate.
            </p>
            <p className="mt-2 text-[11px] text-[#7A7A7A]">Reference · {done?.id}</p>
            <button
              type="button"
              onClick={onClose}
              data-testid="volume-laptop-success-close"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF6600] hover:bg-[#FF8533] text-white text-sm font-bold uppercase tracking-widest transition-colors"
            >
              Close
            </button>
          </div>
        ) : step === "review" ? (
          <ReviewStep
            form={form}
            onBack={() => setStep("form")}
            onSubmit={submit}
            submitting={submitting}
          />
        ) : (
          <div className="p-6 sm:p-8">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF6600]">
              Volume Laptop Solutions Request
            </div>
            <h2 className="mt-1 font-display text-2xl font-bold leading-tight">
              Tell us what your organization needs.
            </h2>
            <p className="mt-2 text-sm text-[#A3A3A3] leading-relaxed">
              Tell us about your team. We'll recommend the right setup and prepare a tailored quote.
            </p>

            <div className="mt-6 space-y-8">
              {/* 01 — About your organization */}
              <Section num="01" title="About Your Organization" icon={Users}>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Your Name" required>
                    <Input
                      testId="vl-contact-name"
                      value={form.contact.name}
                      onChange={(v) => setContact({ name: v })}
                      placeholder="Full name"
                    />
                  </Field>
                  <Field label="Business / Organization Name" required>
                    <Input
                      testId="vl-org-name"
                      value={form.organization_name}
                      onChange={(v) => setField({ organization_name: v })}
                      placeholder="e.g. Aruba International School"
                    />
                  </Field>
                  <Field label="Email" required>
                    <Input
                      testId="vl-contact-email"
                      type="email"
                      value={form.contact.email}
                      onChange={(v) => setContact({ email: v })}
                      placeholder="you@organization.com"
                    />
                  </Field>
                  <Field label="Phone" required>
                    <Input
                      testId="vl-contact-phone"
                      value={form.contact.phone}
                      onChange={(v) => setContact({ phone: v })}
                      placeholder="+297 …"
                    />
                  </Field>
                </div>
                <Field label="Organization Type" required>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {INDUSTRIES.map((o) => {
                      const active = form.industry === o.id;
                      const Icon = o.icon;
                      return (
                        <button
                          key={o.id}
                          type="button"
                          data-testid={`vl-industry-${o.id}`}
                          onClick={() => setField({ industry: o.id })}
                          className={`text-left px-3 py-2.5 rounded-xl border text-[13px] font-medium inline-flex items-center gap-2 transition-colors ${
                            active
                              ? "border-[#FF6600] bg-[#FF6600]/10 text-white"
                              : "border-[#262626] bg-[#1A1A1A] text-[#D1D1D1] hover:border-[#FF6600]/60"
                          }`}
                        >
                          <Icon className="w-4 h-4 shrink-0 text-[#FF6600]" strokeWidth={2} />
                          <span className="truncate">{o.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  {requireOtherIndustry && (
                    <div className="mt-2">
                      <Input
                        testId="vl-industry-other-text"
                        value={form.industry_other}
                        onChange={(v) => setField({ industry_other: v })}
                        placeholder="Tell us about your organization"
                      />
                    </div>
                  )}
                </Field>
              </Section>

              {/* 02 — What your team needs */}
              <Section num="02" title="What Your Team Needs">
                <Field label="Approx. Number Of Laptops" required>
                  <Input
                    testId="vl-quantity"
                    type="number"
                    min="1"
                    value={form.quantity}
                    onChange={(v) => setField({ quantity: v })}
                    placeholder="e.g. 25"
                  />
                </Field>
                <Field label="Intended Use / Team Needs" required>
                  <div className="flex flex-wrap gap-2">
                    {INTENDED_USES.map((u) => {
                      const active = (form.intended_uses || []).includes(u.id);
                      return (
                        <button
                          key={u.id}
                          type="button"
                          data-testid={`vl-use-${u.id}`}
                          onClick={() => setField({
                            intended_uses: active
                              ? form.intended_uses.filter((x) => x !== u.id)
                              : [...(form.intended_uses || []), u.id],
                          })}
                          className={`text-[12px] font-semibold px-3.5 py-2 rounded-full border transition-colors ${
                            active
                              ? "border-[#FF6600] bg-[#FF6600]/15 text-white"
                              : "border-[#262626] bg-[#1A1A1A] text-[#D1D1D1] hover:border-[#FF6600]/60"
                          }`}
                        >
                          {u.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-1.5 text-[11px] text-[#7A7A7A]">Select all that apply.</div>
                  {requireOtherUse && (
                    <div className="mt-2">
                      <Input
                        testId="vl-use-other-text"
                        value={form.intended_use_other}
                        onChange={(v) => setField({ intended_use_other: v })}
                        placeholder="Describe the intended use"
                      />
                    </div>
                  )}
                </Field>
                <Field label="Platform Preference (optional)">
                  <div className="flex flex-wrap gap-2">
                    {PLATFORMS.map((p) => {
                      const active = form.platform === p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          data-testid={`vl-platform-${p.id}`}
                          onClick={() => setField({ platform: active ? "" : p.id })}
                          className={`text-[12px] font-semibold px-3.5 py-2 rounded-full border transition-colors ${
                            active
                              ? "border-[#FF6600] bg-[#FF6600]/15 text-white"
                              : "border-[#262626] bg-[#1A1A1A] text-[#D1D1D1] hover:border-[#FF6600]/60"
                          }`}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              </Section>

              {/* 03 — How you want it prepared */}
              <Section num="03" title="How You Want It Prepared">
                <Field label="How would you like us to help?" required>
                  <div className="grid gap-2">
                    {SETUP_LEVELS.map((l) => {
                      const active = form.setup_level === l.id;
                      return (
                        <button
                          key={l.id}
                          type="button"
                          data-testid={`vl-setup-${l.id}`}
                          onClick={() => setField({ setup_level: l.id })}
                          className={`text-left px-3.5 py-3 rounded-xl border transition-colors ${
                            active
                              ? "border-[#FF6600] bg-[#FF6600]/10"
                              : "border-[#262626] bg-[#1A1A1A] hover:border-[#FF6600]/60"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className={`text-[13px] font-semibold ${active ? "text-white" : "text-[#D1D1D1]"}`}>
                              {l.label}
                            </span>
                            {l.recommended && (
                              <span
                                data-testid={`vl-setup-${l.id}-recommended`}
                                className="shrink-0 text-[9.5px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-[#FF6600]/50 bg-[#FF6600]/10 text-[#FF6600]"
                              >
                                Recommended
                              </span>
                            )}
                          </div>
                          {l.sub && (
                            <p className="mt-1 text-[11.5px] leading-snug text-[#A3A3A3]">{l.sub}</p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </Field>
                <Field label="Software Requirements (optional)">
                  <TextArea
                    testId="vl-software"
                    value={form.software_needs}
                    onChange={(v) => setField({ software_needs: v })}
                    placeholder="Tell us any software your team already uses or needs — e.g. Microsoft 365, Adobe, Autodesk, accounting or school applications."
                    rows={2}
                  />
                </Field>
                <Field label="Desired Timeframe / Deadline (optional)">
                  <Input
                    testId="vl-timing"
                    value={form.timing}
                    onChange={(v) => setField({ timing: v })}
                    placeholder="e.g. Within 4 weeks · Start of school year · Q3 2026"
                  />
                </Field>
                <Field label="Additional Requirements / Notes">
                  <TextArea
                    testId="vl-additional"
                    value={form.additional_info}
                    onChange={(v) => setField({ additional_info: v })}
                    placeholder="Anything else we should know about your team, delivery, setup or requirements?"
                    rows={3}
                  />
                </Field>
              </Section>
            </div>

            <div className="mt-8 pt-6 border-t border-[#262626] flex items-center justify-between gap-3 flex-wrap sm:flex-nowrap">
              <p className="text-[11px] text-[#7A7A7A] leading-relaxed max-w-md">
                Volume orders are quoted individually based on your
                requirements, configuration and availability.
              </p>
              <button
                type="button"
                onClick={() => canSubmit && setStep("review")}
                disabled={!canSubmit}
                data-testid="volume-laptop-review"
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold uppercase tracking-widest transition-colors ${
                  canSubmit
                    ? "bg-[#FF6600] hover:bg-[#FF8533] text-white"
                    : "bg-[#262626] text-[#7A7A7A] cursor-not-allowed"
                }`}
              >
                Review Request
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Review step ----------------------------------------------------------
const industryLabelFor = (id) =>
  INDUSTRIES.find((x) => x.id === id)?.label || id || "—";
const platformLabelFor = (id) =>
  PLATFORMS.find((x) => x.id === id)?.label || id || "—";
const intendedUseLabelFor = (id) =>
  INTENDED_USES.find((x) => x.id === id)?.label || id || "—";
const setupLevelLabelFor = (id) =>
  SETUP_LEVELS.find((x) => x.id === id)?.label || id || "—";

const ReviewStep = ({ form, onBack, onSubmit, submitting }) => {
  const industry = form.industry === "other" && form.industry_other
    ? `Other · ${form.industry_other}`
    : industryLabelFor(form.industry);
  const intended = (() => {
    const uses = form.intended_uses || [];
    if (!uses.length) return "—";
    const labels = uses.map((id) => (
      id === "other" && form.intended_use_other
        ? `Other · ${form.intended_use_other}`
        : intendedUseLabelFor(id)
    ));
    return labels.join(", ");
  })();
  return (
    <div className="p-6 sm:p-8" data-testid="volume-laptop-review-step">
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF6600]">
        Review Your Request
      </div>
      <h2 className="mt-1 font-display text-2xl font-bold leading-tight">
        Everything look right?
      </h2>
      <p className="mt-2 text-sm text-[#A3A3A3] leading-relaxed">
        Confirm the details below — we'll follow up with a tailored quote.
      </p>

      <div className="mt-6 space-y-5">
        <ReviewBlock title="Contact">
          <ReviewRow label="Name" value={form.contact.name} />
          <ReviewRow label="Organization" value={form.organization_name} />
          <ReviewRow label="Email" value={form.contact.email} />
          <ReviewRow label="Phone" value={form.contact.phone} />
        </ReviewBlock>
        <ReviewBlock title="Organization">
          <ReviewRow label="Industry" value={industry} />
          <ReviewRow label="Platform" value={form.platform ? platformLabelFor(form.platform) : "No preference"} />
        </ReviewBlock>
        <ReviewBlock title="Volume">
          <ReviewRow label="Approx. Laptops" value={`~${form.quantity}`} />
          <ReviewRow label="Intended Use" value={intended} />
        </ReviewBlock>
        <ReviewBlock title="Software & Setup">
          <ReviewRow label="Setup Level" value={setupLevelLabelFor(form.setup_level)} />
          {form.software_needs && <ReviewRow label="Software" value={form.software_needs} />}
        </ReviewBlock>
        {(form.timing || form.additional_info) && (
          <ReviewBlock title="Project Details">
            {form.timing && <ReviewRow label="Timeframe" value={form.timing} />}
            {form.additional_info && <ReviewRow label="Notes" value={form.additional_info} />}
          </ReviewBlock>
        )}
      </div>

      <div className="mt-8 pt-6 border-t border-[#262626] flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={onBack}
          data-testid="volume-laptop-back"
          className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3] hover:text-[#FF6600] transition-colors"
        >
          ← Edit
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          data-testid="volume-laptop-submit"
          className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold uppercase tracking-widest transition-colors ${
            submitting
              ? "bg-[#262626] text-[#7A7A7A] cursor-not-allowed"
              : "bg-[#FF6600] hover:bg-[#FF8533] text-white"
          }`}
        >
          {submitting ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
          ) : (
            <>Submit Volume Request <Send className="w-4 h-4" strokeWidth={2} /></>
          )}
        </button>
      </div>
    </div>
  );
};

const ReviewBlock = ({ title, children }) => (
  <div className="rounded-xl border border-[#262626] bg-[#0F0F0F] p-4">
    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#7A7A7A] mb-2">
      {title}
    </div>
    <div className="space-y-1.5">{children}</div>
  </div>
);
const ReviewRow = ({ label, value }) => (
  <div className="flex items-baseline gap-3 text-sm">
    <span className="text-[#7A7A7A] w-32 shrink-0 text-xs">{label}</span>
    <span className="text-[#D1D1D1] break-words min-w-0">{value || "—"}</span>
  </div>
);

const Section = ({ num, title, icon: Icon, children }) => (
  <div>
    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#7A7A7A] inline-flex items-center gap-1.5 mb-3">
      {num && <span className="text-[#FF6600]">{num} —</span>}
      {Icon && <Icon className="w-3 h-3" />} {title}
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const Field = ({ label, required, children }) => (
  <label className="block">
    <div className="text-[11.5px] font-semibold text-[#D1D1D1] mb-1.5">
      {label} {required && <span className="text-[#FF6600]">*</span>}
    </div>
    {children}
  </label>
);

const Input = ({ value, onChange, placeholder, type = "text", min, testId }) => (
  <input
    data-testid={testId}
    type={type}
    min={min}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full bg-[#1A1A1A] border border-[#262626] rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#FF6600] transition-colors"
  />
);

const TextArea = ({ value, onChange, placeholder, rows = 3, testId }) => (
  <textarea
    data-testid={testId}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    className="w-full bg-[#1A1A1A] border border-[#262626] rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#FF6600] resize-none transition-colors"
  />
);

export default VolumeLaptopInquiryModal;
